import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Coins } from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtMoney = (eur) => (eur || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const PAYMENT_LABELS = {
  paypal: "PayPal",
  bank_transfer: "Virement",
  email: "Contact email",
  other: "Autre",
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const download = async (inv) => {
    try {
      const token = localStorage.getItem("vape_token");
      const resp = await fetch(`${API_URL}/invoices/${inv.id}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Téléchargement impossible");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `facture_${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    }
  };

  const totalTTC = invoices.reduce((s, i) => s + (i.amount_eur || 0), 0);

  return (
    <DashboardLayout title="Factures & TVA">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="text-sm text-zinc-400">
          {invoices.length} facture{invoices.length > 1 ? "s" : ""} · Total {fmtMoney(totalTTC)} TTC
        </div>
        <Link to="/credits" data-testid="invoices-buy-link" className="text-sm bg-red-500 hover:bg-red-600 text-white px-4 py-2 font-medium inline-flex items-center gap-2">
          <Coins className="w-4 h-4" /> Acheter des crédits
        </Link>
      </div>

      {!loading && invoices.length === 0 && (
        <div className="mb-6 border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
          Aucune facture pour l'instant. Vos factures apparaîtront ici après votre premier achat de crédits.
          <Link to="/credits" className="text-red-500 hover:text-red-400 ml-2">Voir les packs →</Link>
        </div>
      )}

      <div className="border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 border-b border-zinc-800">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">N° Facture</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Paiement</th>
              <th className="px-4 py-3 text-right">Montant TTC</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-500 text-sm">Chargement…</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtDate(inv.created_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-white font-semibold">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-zinc-300">{inv.credits} crédits d'étiquetage</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border border-zinc-700 text-zinc-400">
                    {PAYMENT_LABELS[inv.payment_method] || inv.payment_method}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-sm text-white font-semibold text-right">{fmtMoney(inv.amount_eur)}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    data-testid={`invoice-download-${inv.id}`}
                    onClick={() => download(inv)}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
